import { Code2, Network, Users, Target, CheckCircle2, ArrowRight, Sparkles, Lightbulb, Shield, TrendingUp, Settings, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";

const ITConsulting = () => {
  const services = [
    {
      icon: Lightbulb,
      title: "Estratégia Digital",
      description: "Planejamento estratégico para transformação digital do seu negócio.",
      color: "from-yellow-500/20 to-yellow-600/20",
      features: [
        "Roadmap tecnológico",
        "Análise de maturidade digital",
        "Definição de KPIs",
        "Plano de transformação"
      ]
    },
    {
      icon: Target,
      title: "Análise de Processos",
      description: "Identificação de gargalos e oportunidades de melhoria.",
      color: "from-blue-500/20 to-blue-600/20",
      features: [
        "Mapeamento de processos",
        "Análise de eficiência",
        "Identificação de gaps",
        "Recomendações práticas"
      ]
    },
    {
      icon: Settings,
      title: "Otimização de Sistemas",
      description: "Melhore a performance e eficiência dos seus sistemas.",
      color: "from-purple-500/20 to-purple-600/20",
      features: [
        "Auditoria de infraestrutura",
        "Otimização de performance",
        "Redução de custos",
        "Escalabilidade"
      ]
    },
    {
      icon: Shield,
      title: "Segurança da Informação",
      description: "Proteja seus dados e sistemas contra ameaças.",
      color: "from-red-500/20 to-red-600/20",
      features: [
        "Avaliação de riscos",
        "Políticas de segurança",
        "Compliance (LGPD, ISO)",
        "Plano de contingência"
      ]
    },
    {
      icon: Network,
      title: "Arquitetura de Soluções",
      description: "Desenho de arquiteturas robustas e escaláveis.",
      color: "from-green-500/20 to-green-600/20",
      features: [
        "Arquitetura de software",
        "Cloud architecture",
        "Microserviços",
        "Integração de sistemas"
      ]
    },
    {
      icon: Users,
      title: "Gestão de TI",
      description: "Estruturação e governança de TI para sua empresa.",
      color: "from-indigo-500/20 to-indigo-600/20",
      features: [
        "ITIL e boas práticas",
        "Gestão de projetos",
        "Service desk",
        "SLA e métricas"
      ]
    }
  ];

  const benefits = [
    {
      title: "Visão Estratégica",
      description: "Alinhamento da TI com os objetivos de negócio"
    },
    {
      title: "Redução de Custos",
      description: "Otimização de recursos e investimentos em tecnologia"
    },
    {
      title: "Aumento de Eficiência",
      description: "Processos mais ágeis e produtivos"
    },
    {
      title: "Mitigação de Riscos",
      description: "Identificação e prevenção de problemas"
    },
    {
      title: "Inovação",
      description: "Adoção de tecnologias emergentes"
    },
    {
      title: "Competitividade",
      description: "Vantagem competitiva através da tecnologia"
    }
  ];

  const approach = [
    {
      step: "01",
      title: "Diagnóstico",
      description: "Avaliação completa do cenário atual de TI",
      icon: FileText
    },
    {
      step: "02",
      title: "Análise",
      description: "Identificação de oportunidades e desafios",
      icon: Target
    },
    {
      step: "03",
      title: "Planejamento",
      description: "Elaboração de estratégias e roadmap",
      icon: Lightbulb
    },
    {
      step: "04",
      title: "Implementação",
      description: "Execução das recomendações com acompanhamento",
      icon: Settings
    },
    {
      step: "05",
      title: "Monitoramento",
      description: "Acompanhamento de resultados e ajustes",
      icon: TrendingUp
    },
    {
      step: "06",
      title: "Evolução Contínua",
      description: "Melhoria contínua e adaptação às mudanças",
      icon: Network
    }
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)] pointer-events-none" />

      {/* Header */}
      <header className="border-b border-border/50 bg-card/30 backdrop-blur-xl sticky top-0 z-50 relative">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/">
            <img src="/logo.png" alt="Coddar Logo" className="h-11 w-auto" />
          </Link>
          <nav className="hidden md:flex gap-6">
            <Link to="/#servicos" className="text-muted-foreground hover:text-primary transition-colors">
              Serviços
            </Link>
            <Link to="/blog" className="text-muted-foreground hover:text-primary transition-colors">
              Blog
            </Link>
            <Link to="/seguranca-digital" className="text-muted-foreground hover:text-primary transition-colors">
              Segurança Digital
            </Link>
            <Link to="/contato" className="text-muted-foreground hover:text-primary transition-colors">
              Contato
            </Link>
          </nav>
          <Button asChild variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 hover:border-primary">
            <a href="https://folio.app.br/area-cliente">Área do Cliente</a>
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 md:py-32 px-4 relative">
        <div className="container mx-auto text-center relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8">
            <ArrowRight className="h-4 w-4 rotate-180" />
            Voltar para Home
          </Link>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6 backdrop-blur-sm">
            <Sparkles className="h-4 w-4 text-primary animate-pulse" />
            <span className="text-sm font-medium text-primary">Consultoria em TI</span>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 max-w-5xl mx-auto leading-tight">
            Transforme TI em{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_auto] animate-[gradient_3s_linear_infinite]">
              vantagem competitiva
            </span>
          </h1>

          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Consultoria especializada para otimizar processos, reduzir custos e impulsionar a inovação
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="gap-2 group">
              <Link to="/contato">
                <span className="flex items-center gap-2">
                  Agendar Diagnóstico <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/contato">Falar com Especialista</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 px-4 relative">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
              Serviços de Consultoria
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Soluções estratégicas para todos os desafios de TI da sua empresa
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card
                  key={index}
                  className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(99,159,255,0.2)] hover:scale-[1.02] group"
                >
                  <CardHeader>
                    <div className={`h-16 w-16 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform relative`}>
                      <Icon className="h-8 w-8 text-primary relative z-10" />
                      <div className="absolute inset-0 bg-primary/30 blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <CardTitle className="text-xl text-foreground">{service.title}</CardTitle>
                    <CardDescription className="text-base text-muted-foreground">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 relative bg-card/30">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Benefícios da Consultoria
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Resultados tangíveis para o seu negócio
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <Card
                key={index}
                className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 group"
              >
                <CardHeader>
                  <CardTitle className="text-lg text-foreground flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    {benefit.title}
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground">
                    {benefit.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-20 px-4 relative">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Nossa Abordagem
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Metodologia comprovada para resultados consistentes
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {approach.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card
                  key={index}
                  className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 group"
                >
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="text-4xl font-bold text-primary/20 group-hover:text-primary/40 transition-colors">
                        {item.step}
                      </div>
                      <div className="flex-1">
                        <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                          <Icon className="h-6 w-6 text-primary" />
                        </div>
                        <CardTitle className="text-lg text-foreground mb-2">{item.title}</CardTitle>
                        <CardDescription className="text-sm text-muted-foreground">
                          {item.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative">
        <div className="container mx-auto relative z-10">
          <Card className="bg-gradient-to-br from-primary/20 via-card/50 to-accent/20 border-primary/30 backdrop-blur-xl overflow-hidden relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/10 to-primary/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
            <CardHeader className="text-center pb-6 relative z-10">
              <CardTitle className="text-3xl md:text-5xl mb-4 text-foreground">
                Pronto para evoluir sua TI?
              </CardTitle>
              <CardDescription className="text-foreground/80 text-lg max-w-2xl mx-auto">
                Agende uma consulta gratuita e descubra como podemos ajudar sua empresa
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center relative z-10">
              <Button size="lg" asChild className="gap-2 group/btn">
                <Link to="/contato">
                  <span className="flex items-center gap-2">
                    Agendar Consulta Gratuita <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default ITConsulting;
